<template>
  <div class="qlc-widget">
    <div v-if="needsNode" class="qlc-widget__top">
      <select :value="nodeId" class="form-control form-control-sm" @change="onNodeChange">
        <option value="">-- Node --</option>
        <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
      </select>
      <button
        v-if="showButtons"
        type="button"
        class="btn btn-sm btn-outline-secondary"
        title="Refresh buttons & status"
        :disabled="!nodeId || loading"
        @click="loadButtons(true)"
      >
        <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'" />
      </button>
    </div>

    <div v-if="showMacros" class="qlc-widget__section qlc-widget__section--macros">
      <div class="qlc-widget__section-title">
        <span>Macros</span>
        <span v-if="visibleMacros.length" class="qlc-widget__hint">{{ visibleMacros.length }}</span>
      </div>
      <div v-if="!visibleMacros.length" class="qlc-widget__empty">
        Belum ada macro.
        <router-link to="/qlc-macros">QLC Macros</router-link>
        /
        <router-link to="/macros">Macros</router-link>
      </div>
      <div v-else class="qlc-macro-list">
        <button
          v-for="item in visibleMacros"
          :key="'m-' + item.id"
          type="button"
          class="qlc-macro-btn"
          :style="macroStyle(item)"
          :disabled="runningMacroId !== null"
          @click="runMacroItem(item)"
        >
          <i
            class="fas"
            :class="runningMacroId === item.id ? 'fa-spinner fa-spin' : 'fa-' + (item.icon || 'play')"
          />
          <span class="qlc-macro-btn__label">{{ item.name }}</span>
          <span class="qlc-macro-btn__count">{{ item.steps.length }}</span>
        </button>
      </div>
    </div>

    <div v-if="showButtons" class="qlc-widget__section">
      <div class="qlc-widget__section-title">
        <span>Buttons</span>
        <span v-if="activeCount" class="qlc-widget__hint qlc-widget__hint--live">{{ activeCount }} active</span>
        <span v-else-if="whitelistActive" class="qlc-widget__hint">{{ visibleButtons.length }}/{{ allButtons.length }}</span>
        <span v-else-if="loading && !visibleButtons.length" class="qlc-widget__hint"><i class="fas fa-spinner fa-spin" /> Loading…</span>
      </div>
      <div v-if="!nodeId" class="qlc-widget__empty">Select a node</div>
      <div v-else-if="!visibleButtons.length && !loading" class="qlc-widget__empty">
        {{ allButtons.length ? 'No buttons in whitelist — edit widget settings' : 'No buttons' }}
      </div>
      <div v-else class="qlc-btn-grid">
        <button
          v-for="b in visibleButtons"
          :key="b.id"
          type="button"
          class="qlc-btn"
          :class="{
            'is-active': buttonState(b.id) >= 200,
            'is-monitor': buttonState(b.id) > 0 && buttonState(b.id) < 200,
            'is-pending': pendingId === b.id,
          }"
          :disabled="pendingId === b.id"
          :title="statusTitle(b)"
          @click="trigger(b)"
        >
          <span class="qlc-btn__dot" aria-hidden="true" />
          <span class="qlc-btn__label">{{ b.name || `Button ${b.id}` }}</span>
        </button>
      </div>
    </div>

    <div v-if="!showButtons && !showMacros" class="qlc-widget__empty">
      Aktifkan Buttons dan/atau Macros di settings widget.
    </div>

    <div v-if="showGrandMaster" class="qlc-widget__gm">
      <div class="qlc-widget__gm-head">
        <label class="small mb-0">Grand Master</label>
        <span class="qlc-widget__gm-val">{{ gmPct }}%</span>
      </div>
      <input
        v-model.number="gm"
        type="range"
        min="0"
        max="255"
        class="custom-range"
        :disabled="!nodeId"
        @input="onGmInput"
        @change="flushGM"
      />
    </div>

    <div v-if="macroStatus" class="qlc-widget__macro-status" :class="macroOk ? 'is-ok' : 'is-err'">
      {{ macroStatus }}
    </div>
    <div v-if="error" class="qlc-widget__error">{{ error }}</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { contrastText } from '@/widgets/macros'
import { getQlcButtons, peekQlcButtons } from '@/widgets/qlcCache'

const POLL_MS = 2000

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
    tabActive: { type: Boolean, default: true },
  },
  data() {
    return {
      allButtons: [],
      statusMap: {},
      gm: 255,
      loading: false,
      pendingId: null,
      runningMacroId: null,
      gmPending: false,
      gmDirty: false,
      gmTimer: null,
      statusInflight: null,
      pollTimer: null,
      error: '',
      macroStatus: '',
      macroOk: false,
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    ...mapGetters('macro', { library: 'macros', qlcMacros: 'qlcMacros' }),
    nodeId() {
      return this.config.nodeId || ''
    },
    showButtons() {
      return this.config.showButtons !== false
    },
    showMacros() {
      return this.config.showMacros === true
    },
    showGrandMaster() {
      return this.config.showGrandMaster !== false
    },
    needsNode() {
      return this.showButtons || this.showGrandMaster
    },
    allowedIds() {
      const raw = this.config.buttonIds
      if (!Array.isArray(raw) || !raw.length) return null
      return new Set(raw.map((id) => Number(id)).filter((n) => Number.isFinite(n)))
    },
    whitelistActive() {
      return !!this.allowedIds
    },
    visibleButtons() {
      if (!this.allowedIds) return this.allButtons
      return this.allButtons.filter((b) => this.allowedIds.has(Number(b.id)))
    },
    activeCount() {
      return this.visibleButtons.filter((b) => this.buttonState(b.id) >= 200).length
    },
    macroPool() {
      const filter = this.config.macroFilter === 'all' ? 'all' : 'qlc'
      return filter === 'all' ? this.library : this.qlcMacros
    },
    visibleMacros() {
      const raw = this.config.macroIds
      const selected = Array.isArray(raw) ? raw.map(Number).filter((n) => Number.isFinite(n)) : []
      if (!selected.length) return this.macroPool
      const set = new Set(selected)
      return this.macroPool.filter((m) => set.has(m.id))
    },
    gmPct() {
      return Math.round((Number(this.gm) / 255) * 100)
    },
  },
  watch: {
    tabActive(active) {
      if (!this.nodeId || !this.showButtons) return
      if (active) this.startPoll()
      else this.stopPoll()
    },
    nodeId: {
      immediate: true,
      handler(id) {
        this.stopPoll()
        this.error = ''
        this.pendingId = null
        if (!id || !this.showButtons) {
          this.allButtons = []
          this.statusMap = {}
          return
        }
        const cached = peekQlcButtons(id)
        if (cached) {
          this.allButtons = cached
          this.refreshStatuses({ force: true })
        } else {
          this.allButtons = []
          this.statusMap = {}
          this.loadButtons(false)
        }
        if (this.tabActive) this.startPoll()
      },
    },
    showButtons(val) {
      if (val && this.nodeId) {
        if (!this.allButtons.length) this.loadButtons(false)
        else this.refreshStatuses({ force: true })
        if (this.tabActive) this.startPoll()
      } else {
        this.stopPoll()
      }
    },
  },
  mounted() {
    if (this.showMacros) this.fetchMacros().catch(() => {})
  },
  beforeDestroy() {
    this.stopPoll()
    if (this.gmTimer) clearTimeout(this.gmTimer)
  },
  methods: {
    ...mapActions('mission', ['sendCommand']),
    ...mapActions('workspace', ['updateWidgetConfig']),
    ...mapActions('macro', ['fetchMacros', 'runMacro']),
    macroStyle(item) {
      const bg = item.color || '#f0ad4e'
      return {
        backgroundColor: bg,
        borderColor: bg,
        color: contrastText(bg),
      }
    },
    buttonState(id) {
      const n = Number(id)
      if (this.statusMap[n] != null) return this.statusMap[n]
      const b = this.allButtons.find((x) => Number(x.id) === n)
      return b && b.status != null ? Number(b.status) : 0
    },
    statusTitle(b) {
      const s = this.buttonState(b.id)
      const state = s >= 200 ? 'Active' : s > 0 ? 'Monitoring' : 'Off'
      return `ID ${b.id} · ${state}`
    },
    onNodeChange(e) {
      if (!this.widgetId) return
      this.updateWidgetConfig({ widgetId: this.widgetId, config: { nodeId: e.target.value } })
    },
    parsePayload(res) {
      const msg = res && res.result
      if (!msg) return null
      if (msg.type === 'error' || msg.error) {
        throw new Error(msg.error || 'Command failed')
      }
      let payload = msg.payload
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (e) {
          return null
        }
      }
      return payload
    },
    async cmd(action, payload = {}) {
      if (!this.nodeId) return null
      const res = await this.sendCommand({ node_id: this.nodeId, adapter: 'qlc', action, payload })
      return this.parsePayload(res)
    },
    applyStatuses(widgets) {
      if (!Array.isArray(widgets)) return
      const next = { ...this.statusMap }
      widgets.forEach((w) => {
        if (w == null || w.id == null) return
        const status = w.status != null ? Number(w.status) : (w.active ? 255 : 0)
        next[Number(w.id)] = status
      })
      this.statusMap = next
    },
    startPoll() {
      this.stopPoll()
      if (!this.showButtons) return
      this.pollTimer = setInterval(() => {
        if (this.pendingId != null || this.runningMacroId != null) return
        this.refreshStatuses({ silent: true })
      }, POLL_MS)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async loadButtons(force = false) {
      if (!this.nodeId || !this.showButtons) return
      if (this.loading) return
      if (!force) {
        const cached = peekQlcButtons(this.nodeId)
        if (cached) {
          this.allButtons = cached
          this.refreshStatuses({ force: true })
          return
        }
      }
      this.loading = true
      this.error = ''
      try {
        const list = await getQlcButtons(
          (payload) => this.sendCommand(payload),
          this.nodeId,
          { force: !!force },
        )
        this.allButtons = list
        this.refreshStatuses({ force: true })
      } catch (e) {
        if (!this.allButtons.length) {
          this.statusMap = {}
        }
        this.error = e.response?.data?.message || e.message || 'Failed to load buttons'
      } finally {
        this.loading = false
      }
    },
    async runMacroItem(item) {
      if (this.runningMacroId !== null || !item?.steps?.length) return
      this.runningMacroId = item.id
      this.macroStatus = ''
      this.macroOk = false
      this.error = ''
      try {
        const res = await this.runMacro(item)
        this.refreshStatuses({ force: true })
        this.macroOk = res.failed === 0
        this.macroStatus = res.failed
          ? `${item.name}: ${res.done} ok, ${res.failed} failed`
          : `${item.name}: ${res.done}/${res.total}`
      } catch (e) {
        this.macroOk = false
        this.macroStatus = e.message || 'Macro failed'
      } finally {
        this.runningMacroId = null
      }
    },
    refreshStatuses(opts = {}) {
      const silent = opts.silent !== false
      const force = !!opts.force
      if (!this.showButtons) return Promise.resolve()
      const ids = this.visibleButtons.map((b) => Number(b.id)).filter((n) => Number.isFinite(n))
      if (!ids.length || !this.nodeId) return Promise.resolve()
      if (this.statusInflight) {
        if (silent) return this.statusInflight
        return this.statusInflight.then(() => {
          if (!force) return
          return this.refreshStatuses({ silent: false, force: true })
        })
      }
      const run = (async () => {
        try {
          const data = (await this.cmd('getWidgetStatuses', { ids, force })) || {}
          this.applyStatuses(data.widgets)
        } catch (e) {
          /* keep last known */
        }
      })()
      this.statusInflight = run
      return run.finally(() => {
        if (this.statusInflight === run) this.statusInflight = null
      })
    },
    async trigger(b) {
      if (!this.nodeId || !b) return
      const id = Number(b.id)
      if (this.pendingId === id) return
      this.pendingId = id
      this.error = ''
      const current = this.buttonState(id)
      const on = current < 200
      const value = on ? 255 : 0
      this.$set(this.statusMap, id, value)
      try {
        const data = await this.cmd('setWidgetState', { widgetId: id, on, value })
        if (data && data.widget) this.applyStatuses([data.widget])
      } catch (e) {
        this.$set(this.statusMap, id, current)
        this.error = e.response?.data?.message || e.message || 'Trigger failed'
        this.refreshStatuses({ force: true })
      } finally {
        this.pendingId = null
      }
    },
    onGmInput() {
      if (this.gmTimer) clearTimeout(this.gmTimer)
      this.gmTimer = setTimeout(() => this.flushGM(), 80)
    },
    async flushGM() {
      if (this.gmTimer) {
        clearTimeout(this.gmTimer)
        this.gmTimer = null
      }
      if (!this.nodeId) return
      if (this.gmPending) {
        this.gmDirty = true
        return
      }
      this.gmPending = true
      this.error = ''
      try {
        do {
          this.gmDirty = false
          await this.cmd('setGrandMaster', { value: Number(this.gm) || 0 })
        } while (this.gmDirty)
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'GM failed'
      } finally {
        this.gmPending = false
      }
    },
  },
}
</script>

<style scoped>
.qlc-widget {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  min-height: 0;
  color: var(--ws-text);
  font-size: 0.8em;
}

.qlc-widget__top {
  display: flex;
  gap: 0.35rem;
  align-items: center;
}

.qlc-widget__top select {
  flex: 1;
  min-width: 0;
}

.qlc-widget__section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qlc-widget__section--macros {
  flex: 0 1 auto;
  max-height: 45%;
}

.qlc-widget__section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.68em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ws-text-muted);
  margin-bottom: 0.35rem;
}

.qlc-widget__hint {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.qlc-widget__hint--live {
  color: var(--ws-accent, #128c7e);
  font-weight: 600;
}

.qlc-widget__empty {
  color: var(--ws-text-muted);
  text-align: center;
  padding: 0.75rem;
  font-size: 0.85em;
}

.qlc-macro-list {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  overflow: auto;
  min-height: 0;
}

.qlc-macro-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 0.4rem 0.55rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
}

.qlc-macro-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qlc-macro-btn__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qlc-macro-btn__count {
  font-size: 0.7em;
  opacity: 0.85;
}

.qlc-btn-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 0.35rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  align-content: start;
}

.qlc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-height: 40px;
  padding: 0.35rem 0.4rem;
  border: 1px solid rgba(127, 127, 127, 0.35);
  background: rgba(127, 127, 127, 0.08);
  color: var(--ws-text);
  border-radius: 4px;
  font-size: 0.72em;
  font-weight: 600;
  line-height: 1.15;
  cursor: pointer;
  overflow: hidden;
}

.qlc-btn__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: rgba(127, 127, 127, 0.45);
}

.qlc-btn__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qlc-btn:not(:disabled):hover {
  background: rgba(37, 99, 235, 0.14);
  border-color: rgba(37, 99, 235, 0.45);
}

.qlc-btn.is-active {
  background: rgba(18, 140, 126, 0.22);
  border-color: var(--ws-accent, #128c7e);
  box-shadow: inset 0 0 0 1px rgba(18, 140, 126, 0.25);
}

.qlc-btn.is-active .qlc-btn__dot {
  background: var(--ws-accent, #128c7e);
  box-shadow: 0 0 0 3px rgba(18, 140, 126, 0.25);
}

.qlc-btn.is-monitor {
  border-color: #f0ad4e;
  background: rgba(240, 173, 78, 0.16);
}

.qlc-btn.is-monitor .qlc-btn__dot {
  background: #f0ad4e;
}

.qlc-btn.is-pending {
  opacity: 0.7;
  cursor: wait;
}

.qlc-btn:disabled:not(.is-pending) {
  opacity: 0.45;
  cursor: not-allowed;
}

.qlc-widget__gm-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.15rem;
}

.qlc-widget__gm label {
  color: var(--ws-text-muted);
}

.qlc-widget__gm-val {
  font-size: 0.72em;
  font-variant-numeric: tabular-nums;
  color: var(--ws-text-muted);
}

.qlc-widget__macro-status {
  font-size: 0.7em;
}
.qlc-widget__macro-status.is-ok { color: #128c7e; }
.qlc-widget__macro-status.is-err { color: #c0392b; }

.qlc-widget__error {
  color: #c0392b;
  font-size: 0.7em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
